export const heroTrustPoints = [
  {
    title: "Verified SAINT® Provider",
    text: "Listed on Magnus Medical's official locator. The only verified outpatient SAINT® provider on the East Coast.",
  },
  {
    title: "Board-certified medical director",
    text: "Care led by Dr. Daniel Pietras, MD, a psychiatrist specializing in treatment-resistant depression.",
  },
  {
    title: "Placebo-controlled results",
    text: "79% remission vs 13% sham in a double-blind randomized trial. Average time to remission: 2.6 days.",
  },
];

export const comparisonRows = [
  {
    label: "Developer",
    saint: "Magnus Medical (developed at Stanford)",
    neurostar: "Neuronetics, Inc.",
  },
  {
    label: "FDA status",
    saint: "FDA-cleared, September 2022 (Breakthrough Device designation)",
    neurostar:
      "FDA-cleared for depression since 2008 — the first TMS device cleared in the U.S.",
  },
  {
    label: "Cleared for",
    saint:
      "Adults with major depressive disorder who did not get adequate relief from antidepressant medication in the current episode",
    neurostar:
      "Major depressive disorder, anxious depression, and OCD (adjunct); adolescents 15–21 for depression (2024)",
  },
  {
    label: "Ages",
    saint: "Adults 22+",
    neurostar: "Adults; adolescents 15–21 for depression",
  },
  {
    label: "How the target is chosen",
    saint:
      "Resting-state fMRI of your own brain, with a proprietary algorithm that picks the stimulation site from your individual connectivity",
    neurostar:
      "Figure-8 coil positioned over the left dorsolateral prefrontal cortex using scalp measurements from the motor-threshold location (often the '5.5 cm rule' or a similar landmark method)",
  },
  {
    label: "Treatment schedule",
    saint:
      "5 consecutive days, 10 short sessions per day (about 10 minutes each) with 50-minute breaks",
    neurostar:
      "One session per day, 5 days a week, for 6 weeks, plus a taper — typically 36 sessions of about 19 minutes (shorter theta-burst options exist)",
  },
  {
    label: "Total course",
    saint: "50 sessions in 1 week",
    neurostar: "36 sessions over about 6–9 weeks",
  },
  {
    label: "Clinical evidence",
    saint:
      "Double-blind, sham-controlled RCT: 79% remission vs 13% sham (Cole et al., Am J Psychiatry 2022). Open-label: 90% remission, average 2.6 days to remission (Cole et al. 2020)",
    neurostar:
      "Pivotal RCT supporting 2008 clearance. Company-reported real-world registry: 83% response and 62% remission among patients completing treatment (clinician-rated); 69% response and 36% remission on patient-rated scales",
  },
  {
    label: "Insurance & cost",
    saint:
      "Self-pay is most common. BCBS in select states and the BCBS Federal Employee Program in select states; some patients pursue reimbursement",
    neurostar:
      "Broadly covered by commercial insurance, Medicare, and the VA for treatment-resistant depression",
  },
  {
    label: "Where it's available",
    saint:
      "Limited number of Verified SAINT® Providers nationwide. Neuro Wellness is the only verified outpatient SAINT provider on the East Coast",
    neurostar: "Thousands of NeuroStar systems in clinics nationwide",
  },
];

export const verdicts = [
  {
    title: "Treatment length",
    text: "5 days vs 36 sessions over about 6–9 weeks",
  },
  {
    title: "Targeting",
    text: "Your fMRI vs scalp landmarks",
  },
  {
    title: "Evidence",
    text: "Placebo-controlled RCT, 79% remission",
  },
];

export const keyDifferences = [
  {
    title: "The calendar",
    isVisual: true,
    text: "Each square is one weekday. Filled squares are days you come in for treatment.",
    footnote:
      "SAINT® sessions are about 10 minutes each, ten per day, with 50-minute breaks in between — you stay awake, and most patients read, work, or rest between sessions.",
  },
  {
    title: "Five days versus six-plus weeks",
    isVisual: false,
    text: "A standard NeuroStar course is one 19-minute session a day, five days a week, for six weeks, followed by a taper — 36 visits in all. SAINT® delivers 50 short sessions in five consecutive days. For people who can't step away from work or family for two months, or who are in a severe episode and can't wait, that timeline is the reason they seek out SAINT®.",
  },
  {
    title: "How the coil finds its spot",
    isVisual: false,
    text: "NeuroStar targets the left dorsolateral prefrontal cortex using scalp landmarks measured from the point that makes your thumb twitch. It works well for many people, but the 'right' spot varies from brain to brain. SAINT® replaces the ruler with a resting-state fMRI and an algorithm that computes your individual target from how your prefrontal cortex connects to deeper mood circuits.",
  },
  {
    title: "Comparing the outcome data",
    isVisual: false,
    text: "NeuroStar's most-cited numbers — 83% response, 62% remission — come from a large real-world registry of patients who completed treatment, without a sham comparison. SAINT®'s 79% remission figure comes from a smaller double-blind, sham-controlled trial where the sham group reached 13%. A registry counts everyone who finished; a placebo-controlled trial proves the treatment itself is doing the work. SAINT® has the harder kind of evidence — and delivered it in five days.",
  },
];

export const whyItMatters = [
  {
    title: "One week instead of six to nine.",
    text: "SAINT® is 50 sessions in five consecutive days; NeuroStar is 36 visits spread over more than two months.",
  },
  {
    title: "Your target from your own fMRI.",
    text: "NeuroStar places the coil using a ruler from the spot that makes your thumb twitch — a population average, not your brain.",
  },
  {
    title: "Placebo-controlled proof.",
    text: "SAINT®'s 79% remission came from a double-blind, sham-controlled trial. NeuroStar's 83%/62% headline numbers come from a company registry of patients who finished treatment, with no sham comparison — and its patient-rated remission rate is 36%.",
  },
  {
    title: "Relief in days, not weeks.",
    text: "Average time to remission was 2.6 days in the SAINT® open-label study.",
  },
  {
    title: "A path forward if NeuroStar already failed you.",
    text: "Prior standard TMS does not rule you out for SAINT®.",
  },
];

export const faqs = [
  {
    q: "Is SAINT® better than NeuroStar?",
    a: "On the measures that matter most to someone with treatment-resistant depression — how the target is chosen, how fast relief comes, and how strong the evidence is — SAINT® leads. It's fMRI-guided, finishes in five days, and showed 79% remission against a placebo. NeuroStar's advantage is insurance coverage, and if that's the deciding factor for you, we provide standard TMS in our clinic as well.",
  },
  {
    q: "I already finished NeuroStar and I'm still depressed. Is SAINT® an option?",
    a: "Often, yes. A prior course of standard TMS does not exclude you from SAINT®, and a meaningful share of our SAINT® patients have already tried it. Bring your records; our medical director will review your history and fMRI safety.",
  },
  {
    q: "Why is SAINT® so much faster? Is it just more sessions per day?",
    a: "Partly. SAINT® uses intermittent theta-burst stimulation ten times a day with 50-minute breaks, which in the trials produced a faster and larger effect than once-daily stimulation. The other half is targeting: each patient's stimulation site is computed from their own fMRI rather than from scalp measurements.",
  },
  {
    q: "Is standard TMS still a good option?",
    a: "Standard TMS still helps many people, and it's well covered by insurance, which is why we keep it available. But if you're eligible for SAINT® and can access it, it is the faster, more precisely targeted option with the stronger evidence. If SAINT® isn't possible for you, we'll help you start standard TMS here instead.",
  },
];

export const sources = [
  {
    n: 1,
    text: "Cole EJ et al. Stanford Neuromodulation Therapy (SNT): a double-blind randomized controlled trial. Am J Psychiatry 2022.",
    url: "https://pubmed.ncbi.nlm.nih.gov/35624717/",
  },
  {
    n: 2,
    text: "Cole EJ et al. Stanford Accelerated Intelligent Neuromodulation Therapy for treatment-resistant depression. Am J Psychiatry 2020.",
    url: "https://pubmed.ncbi.nlm.nih.gov/32552917/",
  },
  {
    n: 3,
    text: "Neuronetics, NeuroStar results (company-reported registry outcomes).",
    url: "https://neurostar.com/hcp/results/",
  },
  {
    n: 4,
    text: "Neuronetics, NeuroStar TMS therapy overview and FDA indications.",
    url: "https://neurostar.com/",
  },
  {
    n: 5,
    text: "Croarkin PE et al. Effectiveness of TMS in adolescents and young adults with MDD (NeuroStar registry). 2025.",
    url: "https://pubmed.ncbi.nlm.nih.gov/41367968/",
  },
];

export const competitorNotice =
  "Neuro Wellness TMS Centers of America is a Verified SAINT® Provider and is not affiliated with, endorsed by, or sponsored by Neuronetics, Inc. NeuroStar Advanced Therapy is a trademark of its respective owner and is referenced here for comparison purposes only. SAINT® and SNT™ are trademarks of Magnus Medical, Inc. This page is educational and is not medical advice. Results from clinical studies reflect the populations studied; individual results vary and no outcome is guaranteed.";
